/**
 * PWA 外部导航状态恢复模块
 *
 * 解决 iOS PWA 的核心问题：
 * 1. window.open 会触发 SFSafariViewController，返回时有白屏"完成"按钮
 * 2. location.href 跳转后，iOS 可能回收 WebView，返回时页面重载
 *
 * 解决策略：
 * - iOS PWA 使用 location.href 跳转（避免白屏）
 * - 跳转前保存快照（路由、滚动位置、业务状态）
 * - 返回时通过 pageshow/visibilitychange 事件检测并恢复状态
 * - 即使页面重载，也能从 sessionStorage 恢复到离开前状态
 */

import { nextTick } from "vue";

// ============================================================================
// 常量定义
// ============================================================================
const STORAGE_KEY = "pwa_nav_snapshot";
const SNAPSHOT_EXPIRE_TIME = 30 * 60 * 1000; // 快照有效期 30 分钟

// ============================================================================
// 工具函数
// ============================================================================

/**
 * 检测是否在 iOS PWA Standalone 模式
 */
export function isIOSPWAStandalone() {
  return (
    window.navigator.standalone === true &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}

/**
 * 检测是否为 URL Scheme（非 http/https 协议）
 */
function isURLScheme(url) {
  if (!url) return false;
  return /^(?!https?:\/\/)[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url);
}

/**
 * 安全读取 sessionStorage
 */
function safeGetStorage(key) {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.warn("[pwaNav] Failed to read sessionStorage:", e);
    return null;
  }
}

/**
 * 安全写入 sessionStorage
 */
function safeSetStorage(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.warn("[pwaNav] Failed to write sessionStorage:", e);
    return false;
  }
}

/**
 * 安全删除 sessionStorage
 */
function safeRemoveStorage(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
}

// ============================================================================
// 快照管理
// ============================================================================

/**
 * 保存快照
 * @param {object} options - 额外的业务状态
 * @param {object} options.taskState - 任务状态（任务组件使用）
 * @param {object} options.customState - 自定义业务状态
 */
export function saveSnapshot(options = {}) {
  const snapshot = {
    timestamp: Date.now(),
    // 路由信息
    route: {
      path: window.location.pathname,
      hash: window.location.hash,
      search: window.location.search,
      fullPath: window.location.pathname + window.location.search + window.location.hash,
    },
    // 滚动位置
    scrollY: window.scrollY,
    scrollX: window.scrollX,
    // 任务状态（用于任务组件恢复）
    taskState: options.taskState || null,
    // 自定义业务状态
    customState: options.customState || null,
  };

  safeSetStorage(STORAGE_KEY, snapshot);
  console.log("[pwaNav] Snapshot saved:", snapshot);
  return snapshot;
}

/**
 * 获取快照
 * @returns {object|null} 快照数据，过期或不存在返回 null
 */
export function getSnapshot() {
  const snapshot = safeGetStorage(STORAGE_KEY);

  if (!snapshot) {
    return null;
  }

  // 检查是否过期
  if (Date.now() - snapshot.timestamp > SNAPSHOT_EXPIRE_TIME) {
    console.log("[pwaNav] Snapshot expired, removing");
    safeRemoveStorage(STORAGE_KEY);
    return null;
  }

  return snapshot;
}

/**
 * 清除快照
 */
export function clearSnapshot() {
  safeRemoveStorage(STORAGE_KEY);
  console.log("[pwaNav] Snapshot cleared");
}

/**
 * 恢复快照
 * @param {object} router - Vue Router 实例
 * @param {object} options - 恢复选项
 * @param {function} options.onTaskRestore - 任务状态恢复回调
 * @param {function} options.onCustomRestore - 自定义状态恢复回调
 * @returns {boolean} 是否成功恢复
 */
export async function restoreSnapshot(router, options = {}) {
  const snapshot = getSnapshot();

  if (!snapshot) {
    console.log("[pwaNav] No snapshot to restore");
    return false;
  }

  console.log("[pwaNav] Restoring snapshot:", snapshot);

  try {
    // 1. 恢复路由
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (snapshot.route.fullPath && snapshot.route.fullPath !== currentPath) {
      console.log("[pwaNav] Restoring route:", snapshot.route.fullPath);
      await router.replace(snapshot.route.fullPath);
    }

    // 2. 恢复滚动位置（延迟执行，等待 DOM 渲染）
    await nextTick();
    setTimeout(() => {
      window.scrollTo(snapshot.scrollX || 0, snapshot.scrollY || 0);
      console.log("[pwaNav] Scroll restored to:", snapshot.scrollX, snapshot.scrollY);
    }, 100);

    // 3. 恢复任务状态
    if (snapshot.taskState && typeof options.onTaskRestore === "function") {
      options.onTaskRestore(snapshot.taskState);
      console.log("[pwaNav] Task state restored:", snapshot.taskState);
    }

    // 4. 恢复自定义状态
    if (snapshot.customState && typeof options.onCustomRestore === "function") {
      options.onCustomRestore(snapshot.customState);
      console.log("[pwaNav] Custom state restored:", snapshot.customState);
    }

    // 5. 清除已使用的快照
    clearSnapshot();

    return true;
  } catch (e) {
    console.error("[pwaNav] Failed to restore snapshot:", e);
    clearSnapshot();
    return false;
  }
}

// ============================================================================
// 外部链接跳转
// ============================================================================

/**
 * 通过隐藏 iframe 触发 URL Scheme
 * 用于 tg://, fb-messenger:// 等协议
 */
function openURLSchemeViaIframe(schemeUrl) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.src = schemeUrl;
  document.body.appendChild(iframe);

  setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, 500);
}

/**
 * 检测是否为 iOS 设备（包括 Safari 和 PWA）
 */
export function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * 检测是否为 PWA 环境
 */
export function isPWA() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * 安全跳转外部链接（同步版本，适配 Safari）
 *
 * 核心原则：Safari 要求 window.open 必须在用户点击事件的同步调用栈中执行，
 * 任何 await / setTimeout / Promise 都会导致 window.open 被静默拦截。
 *
 * 策略：
 * - iOS + PWA standalone：先同步 window.open("about:blank") 预开空白窗口，
 *   再设置 newWin.location.href，这样可以保活当前 PWA 不被替换。
 *   若被拦截则 fallback 到 location.href。
 * - iOS Safari（非 PWA）：使用 location.href（简单可靠）
 * - 其他环境：window.open，若被拦截 fallback 到 location.href
 *
 * URL Scheme 特殊处理：
 * - iOS PWA: 使用 iframe 触发，不离开页面
 *
 * @param {string} url - 要跳转的链接
 * @param {object} options - 配置选项
 * @param {object} options.taskState - 任务状态（任务组件使用）
 * @param {object} options.customState - 自定义业务状态
 */
export function goOut(url, options = {}) {
  if (!url) {
    console.warn("[pwaNav] goOut: URL is empty");
    return;
  }

  const { taskState = null, customState = null } = options;

  const urlIsScheme = isURLScheme(url);
  const isiOS = isIOS();
  const isiOSPWA = isIOSPWAStandalone();

  // iOS PWA + URL Scheme: 使用 iframe 触发，不离开页面
  if (isiOSPWA && urlIsScheme) {
    console.log("[pwaNav] iOS PWA URL Scheme via iframe:", url);
    openURLSchemeViaIframe(url);
    return;
  }

  // 保存快照（用于返回时恢复状态）
  if (taskState || customState) {
    saveSnapshot({ taskState, customState });
  }

  // iOS PWA standalone：同步预开空白窗口，再设置 href（保活 PWA）
  if (isiOSPWA) {
    console.log("[pwaNav] iOS PWA: sync window.open + location.href:", url);
    const newWin = window.open("about:blank", "_blank");
    if (newWin) {
      newWin.location.href = url;
    } else {
      // 被拦截则 fallback 到 location.href
      console.warn("[pwaNav] window.open blocked, fallback to location.href");
      window.location.href = url;
    }
    return;
  }

  // iOS Safari（非 PWA）：使用 location.href
  if (isiOS) {
    console.log("[pwaNav] iOS Safari: location.href:", url);
    window.location.href = url;
    return;
  }

  // 非 iOS：window.open，若被拦截 fallback 到 location.href
  console.log("[pwaNav] Non-iOS: window.open:", url);
  const newWin = window.open(url, "_blank", "noopener,noreferrer");
  if (!newWin) {
    console.warn("[pwaNav] window.open blocked, fallback to location.href");
    window.location.href = url;
  }
}

// ============================================================================
// 事件监听初始化
// ============================================================================

let isInitialized = false;
let routerInstance = null;
let restoreOptions = {};

/**
 * 初始化 PWA 导航恢复监听
 * 应在 router.isReady() 之后调用
 *
 * @param {object} router - Vue Router 实例
 * @param {object} options - 恢复选项
 * @param {function} options.onTaskRestore - 任务状态恢复回调
 * @param {function} options.onCustomRestore - 自定义状态恢复回调
 */
export function initPwaNavRestore(router, options = {}) {
  if (isInitialized) {
    console.warn("[pwaNav] Already initialized");
    return;
  }

  routerInstance = router;
  restoreOptions = options;
  isInitialized = true;

  // 监听 pageshow 事件
  // 当页面从 bfcache 恢复或重新加载时触发
  window.addEventListener("pageshow", handlePageShow);

  // 监听 visibilitychange 事件
  // 当用户从后台切回 PWA 时触发
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // 页面加载时检查是否需要恢复
  checkAndRestore();

  console.log("[pwaNav] Initialized");
}

/**
 * pageshow 事件处理
 * persisted 为 true 表示页面从 bfcache 恢复
 */
function handlePageShow(event) {
  console.log("[pwaNav] pageshow event, persisted:", event.persisted);

  if (event.persisted) {
    // 从 bfcache 恢复，可能不需要做什么
    // 但某些情况下状态可能丢失，尝试恢复
    checkAndRestore();
  }
}

/**
 * visibilitychange 事件处理
 */
function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    console.log("[pwaNav] Page became visible");
    // 延迟执行，避免与其他事件冲突
    setTimeout(() => {
      checkAndRestore();
    }, 100);
  }
}

/**
 * 检查并恢复快照
 */
function checkAndRestore() {
  if (!routerInstance) {
    console.warn("[pwaNav] Router not initialized");
    return;
  }

  const snapshot = getSnapshot();
  if (snapshot) {
    console.log("[pwaNav] Found snapshot, attempting restore");
    restoreSnapshot(routerInstance, restoreOptions);
  }
}

/**
 * 销毁监听（通常不需要调用）
 */
export function destroyPwaNavRestore() {
  window.removeEventListener("pageshow", handlePageShow);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  isInitialized = false;
  routerInstance = null;
  restoreOptions = {};
  console.log("[pwaNav] Destroyed");
}

// ============================================================================
// 导出
// ============================================================================

export default {
  isIOS,
  isPWA,
  isIOSPWAStandalone,
  saveSnapshot,
  getSnapshot,
  clearSnapshot,
  restoreSnapshot,
  goOut,
  initPwaNavRestore,
  destroyPwaNavRestore,
};
